import type { Href } from 'expo-router';

import { getLaunchParam } from '@/modules/clientContext';

import { PERSISTENT_LAUNCH_PARAMS } from '../constants/persistentLaunchParams';

/**
 * Append the persistent launch params (the `PERSISTENT_LAUNCH_PARAMS` allowlist)
 * to a navigation target so they persist in the URL across the whole flow —
 * mirroring how Sparta keeps them in the query string. The params themselves are
 * captured in `@/modules/clientContext`; keeping them in the URL is a navigation
 * concern, so this lives with the other Href utilities (`hrefPathname`).
 *
 * `currentRoute` is kept as a bare logical path (so route-matching comparisons
 * via `hrefPathname` keep working); this is applied ONLY at the two URL-writing
 * chokepoints (app/index.tsx + the (onboarding) layout's `router.replace` /
 * `<Redirect>`), which every navigation funnels through. Existing params on the
 * href (e.g. an entry `?org=`) are preserved.
 *
 * No-op when none of the allowlisted params were captured (not launched embedded)
 * — returns the href unchanged so those sessions keep clean URLs.
 *
 * Returns a fully-formed query STRING (`/step/x?…`) rather than the object
 * `{ pathname, params }` form: expo-router's object-param serialization proved
 * unreliable here (it kept `vida_client` but dropped `vida_client_version` on the
 * URL write), so we build the exact query ourselves to guarantee all land.
 */
export function appendLaunchParamsToHref(href: Href): Href {
  const persisted = PERSISTENT_LAUNCH_PARAMS.flatMap((key) => {
    const value = getLaunchParam(key);
    return value === undefined ? [] : [[key, value] as const];
  });
  // Also bail if `URLSearchParams` is unavailable (not guaranteed on every native
  // JS runtime). In practice `persisted` is already empty there — the launchParams
  // capture guards the same way — but this keeps the util self-contained.
  if (persisted.length === 0 || typeof URLSearchParams === 'undefined') return href;

  const search = new URLSearchParams();
  let pathname: string;

  if (typeof href === 'string') {
    const queryIndex = href.indexOf('?');
    pathname = queryIndex >= 0 ? href.slice(0, queryIndex) : href;
    if (queryIndex >= 0) {
      new URLSearchParams(href.slice(queryIndex + 1)).forEach((value, key) => {
        search.set(key, value);
      });
    }
  } else {
    pathname = (href.pathname ?? '') as string;
    Object.entries(href.params ?? {}).forEach(([key, value]) => {
      search.set(key, String(value));
    });
  }

  for (const [key, value] of persisted) {
    search.set(key, value);
  }

  const queryString = search.toString();
  return (queryString ? `${pathname}?${queryString}` : pathname) as Href;
}
